/**
 * RenameModal — renomeia uma OU ou um ambiente. Task 7 do plano
 * `2026-08-26-renomear-tenant-e-ou` (TASK-10149).
 *
 * DUAS DECISOES QUE VALEM LER ANTES DE MEXER
 *
 * 1. **O e-mail derivado vem do servidor, nao de uma copia da regra aqui.**
 *    O plano propunha um `emailDerivado()` em TypeScript espelhando
 *    `provisioning_service._email()`. O proprio backend explica por que isso
 *    da errado: `derivar_alvo` reusa o `_email()` do provisionamento "de
 *    proposito: se o rename derivasse por conta propria, as duas regras
 *    divergiriam no primeiro ajuste, e a divergencia so apareceria em
 *    producao". Uma terceira copia teria o mesmo defeito, com o agravante de a
 *    tela mostrar um endereco e o job usar outro.
 *
 * 2. **O conflito nao tem como ser sabido no navegador.** `livre`,
 *    `mesma_conta`, `outra_conta` e `fora_do_padrao` dependem do estado da
 *    AWS, e sao justamente o que impede o operador de confirmar um rename que
 *    vai ser recusado. Por isso a previa e uma chamada, e nao uma conta de
 *    cabeca.
 *
 * A previa roda o MESMO `preparar_rename_ambiente` que o passo 0 do job roda.
 */

import { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Code,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  TextInput,
} from '@mantine/core'
import { IconAlertTriangle } from '@tabler/icons-react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { useNavigate } from 'react-router-dom'
import {
  awsOrgProvisioningApi,
  CONFLITOS_QUE_IMPEDEM,
  type ConflitoEmail,
} from '@/api/awsOrgProvisioning'
import { handleProvisioningActionError } from './handleProvisioningActionError'

export interface RenameModalProps {
  opened: boolean
  /** `ou` renomeia so a OU; `ambiente` renomeia conta e e-mail raiz junto. */
  tipo: 'ou' | 'ambiente'
  nomeAtual: string
  onClose: () => void
  /** Obrigatorio quando `tipo === 'ambiente'`. */
  tenantId?: number
  /** Obrigatorio quando `tipo === 'ou'`. */
  ouId?: string
  onSuccess?: () => void
}

/** Motivo, em portugues, de por que o rename esta bloqueado. */
const MOTIVO_DO_CONFLITO: Record<ConflitoEmail, string | null> = {
  livre: null,
  mesma_conta: null,
  outra_conta:
    'O e-mail derivado está preso a outra conta AWS. A AWS recusaria a troca, ' +
    'e nenhum reuso muda isso — escolha outro nome.',
  fora_do_padrao:
    'O e-mail derivado não é o que a convenção de provisionamento geraria. ' +
    'Renomear assim criaria um endereço que ninguém consegue explicar depois.',
}

const DEBOUNCE_MS = 400

export function RenameModal({
  opened,
  tipo,
  nomeAtual,
  onClose,
  tenantId,
  ouId,
  onSuccess,
}: RenameModalProps) {
  const navigate = useNavigate()
  const [novoNome, setNovoNome] = useState('')
  const [confirmacao, setConfirmacao] = useState('')
  const [nomeDebounced, setNomeDebounced] = useState('')

  useEffect(() => {
    const t = setTimeout(() => setNomeDebounced(novoNome.trim()), DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [novoNome])

  // Renomear OU nao mexe em e-mail nenhum — nao ha previa a pedir.
  const querPrevia =
    tipo === 'ambiente' && tenantId != null && nomeDebounced.length > 0

  const { data: previa, isFetching: carregandoPrevia } = useQuery({
    queryKey: ['rename-preview', tenantId, nomeDebounced],
    queryFn: () =>
      awsOrgProvisioningApi.renamePreview(tenantId as number, nomeDebounced),
    enabled: querPrevia,
    retry: false,
  })

  const conflito = previa?.conflito
  const bloqueado = conflito
    ? CONFLITOS_QUE_IMPEDEM.includes(conflito)
    : false
  const motivo = conflito ? MOTIVO_DO_CONFLITO[conflito] : null

  const confirmacaoBate =
    confirmacao.trim().toLowerCase() === nomeAtual.trim().toLowerCase()
  const mesmoNome =
    novoNome.trim().toLowerCase() === nomeAtual.trim().toLowerCase()

  const podeRenomear =
    novoNome.trim() !== '' && confirmacaoBate && !mesmoNome && !bloqueado

  const renomear = useMutation({
    mutationFn: async () => {
      if (tipo === 'ou') {
        return awsOrgProvisioningApi.renameOu(
          ouId as string,
          novoNome.trim(),
          confirmacao.trim(),
        )
      }
      return awsOrgProvisioningApi.renameEnvironment(
        tenantId as number,
        novoNome.trim(),
        confirmacao.trim(),
      )
    },
    onSuccess: (r) => {
      notifications.show({
        title: 'Renomeação agendada',
        message: `Job #${r.job_id} criado.`,
        color: 'blue',
      })
      onSuccess?.()
      onClose()
      navigate(`/admin/tenants/jobs/${r.job_id}`)
    },
    onError: handleProvisioningActionError,
  })

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Renomear ${tipo === 'ou' ? 'unidade organizacional' : 'ambiente'}`}
    >
      <Stack gap="sm">
        <TextInput
          label="Novo nome"
          value={novoNome}
          onChange={(e) => setNovoNome(e.currentTarget.value.toUpperCase())}
          error={mesmoNome && novoNome ? `Já se chama ${nomeAtual}.` : null}
          data-autofocus
        />

        {tipo === 'ambiente' && nomeDebounced && (
          <Alert
            color={bloqueado ? 'yellow' : 'blue'}
            icon={bloqueado ? <IconAlertTriangle size={16} /> : undefined}
          >
            <Stack gap={4}>
              <Group gap="xs">
                <Text size="sm">O e-mail raiz da conta passará a ser</Text>
                {carregandoPrevia && !previa ? (
                  <Loader size="xs" />
                ) : (
                  <Code>{previa?.novo_email ?? '—'}</Code>
                )}
              </Group>
              {motivo && (
                <Text size="sm" fw={600}>
                  {motivo}
                </Text>
              )}
              {conflito === 'mesma_conta' && (
                <Text size="xs" c="dimmed">
                  A conta já usa esse endereço — os passos de troca de e-mail
                  serão pulados.
                </Text>
              )}
            </Stack>
          </Alert>
        )}

        <TextInput
          label={`Digite ${nomeAtual} para confirmar`}
          value={confirmacao}
          onChange={(e) => setConfirmacao(e.currentTarget.value)}
        />

        <Text size="xs" c="dimmed">
          Esta ação altera a AWS e não tem desfazer imediato.
        </Text>

        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            disabled={!podeRenomear}
            loading={renomear.isPending}
            onClick={() => renomear.mutate()}
          >
            Renomear
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
